import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import 'hammerjs';
import 'moment/min/locales';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';

import { CommonModule } from '@angular/common';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routing';
import { HomeModule } from './core/home/home.module';
import { AuthenticationModule } from './features/authentication/authentication.module';
import { HttpConfigInterceptor } from './shared/http-config.interceptor';
import { AssetManagementState } from './shared/state/AssetManagementState';
import { CustomerInfoState } from './shared/state/CustomerInfoState';
import { DealerState } from './shared/state/DealerState';
import { ProductsState } from './shared/state/ProductsState';
import { TranslationState } from './shared/state/TranslationState';
import { LayoutModule } from './core/layout/layout.module';
import { UserManagementState } from './shared/state/UserManagementState';
import { UserState } from './shared/state/UserState';
import { WorkOrderState } from './shared/state/WorkOrderState';
import { CasingSearchState } from './shared/state/CasingSearchState';
import { KubeJobsState } from './shared/state/KubeJobsState';
import { ProductDataModule } from './features/product-data/product-data.module';
import { SetupScreensModule } from './features/setup-screens/setup-screens.module';
import { PacklistsModule } from './features/packlists/packlists.module';
import { LogsModule } from './features/logs/logs.module';
import { DatasetDownloadModule } from './features/dataset-download/dataset-download.module';
import { InventoryModule } from './features/inventory/inventory.module';
import { SearchModule } from './features/search/search.module';
import { WorkorderModule } from './features/workorder/workorder.module';
import { CasingDropdownDataState } from './shared/state/CasingDropdownState';
import { CustomDateAdapter, DYNAMIC_MAT_DATE_FORMATS } from './shared/services/custom-date-adapter';
import { AlertService } from '@michelin/theme';
import { CustomAlertService } from './shared/services/custom-alert.service';
import { MAT_DATE_FORMATS, DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { AdministrationModule } from './features/administration/administration.module';
import { HelpModule } from './features/help/help.module';

@NgModule({
  imports: [
    BrowserAnimationsModule,
    CommonModule,
    LayoutModule,
    AppRoutingModule,
    HomeModule,
    ProductDataModule,
    PacklistsModule,
    LogsModule,
    DatasetDownloadModule,
    HelpModule,
    InventoryModule,
    SearchModule,
    WorkorderModule,
    SetupScreensModule,
    AuthenticationModule,
    AdministrationModule,
    MatButtonModule,
    MatDatepickerModule,
    MatTimepickerModule,
    MatNativeDateModule,
    NgxsModule.forRoot(
      [
        CustomerInfoState,
        AssetManagementState,
        TranslationState,
        ProductsState,
        DealerState,
        UserManagementState,
        UserState,
        WorkOrderState,
        CasingDropdownDataState,
        CasingSearchState,
        KubeJobsState
      ],
      {
        developmentMode: !environment.production
      }
    ),
    NgxsFormPluginModule.forRoot(),
    NgxsRouterPluginModule.forRoot(),
    NgxsStoragePluginModule.forRoot({
      keys: [
        'dealer',
        'translations',
        'userManagement',
        'Products',
        'user',
        'currentCustomerAssets',
        'currentCustomer',
        'workOrder',
        'casingDropdownData',
        'casingSearch',
        'kubeJobs',
        'logs'
      ]
    }),
    NgxsLoggerPluginModule.forRoot({ disabled: environment.production }), // This should always be the 2nd last NGXS import
    NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.production }) // This should always be the last NGXS import
  ],
  declarations: [AppComponent],
  exports: [],
  bootstrap: [AppComponent],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpConfigInterceptor,
      multi: true
    },
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: CustomDateAdapter, useExisting: DateAdapter }, // alias so injecting CustomDateAdapter works
    { provide: MAT_DATE_FORMATS, useValue: DYNAMIC_MAT_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
    { provide: AlertService, useClass: CustomAlertService } // Override AlertService with custom implementation to control warning behavior
  ]
})
export class AppModule {}
